/**
 * Vue.js міксин для відстеження видимості товарів
 * Використовується в компонентах, які відображають списки товарів
 */
import ProductVisibilityTracker from '@/utils/productVisibilityTracker';
export default {
    data() {
        return {
            productTracker: null
        };
    },
    mounted() {
        this.initProductTracker();
    },
    beforeDestroy() {
        if (this.productTracker) {
            this.productTracker.destroy();
        }
    },
    methods: {
        /**
         * Ініціалізує відстеження товарів
         * @param {string} listName - Назва списку товарів
         * @param {string} listId - ID списку товарів
         */
        initProductTracker(listName = 'Product Card', listId = 'product-card') {
            this.productTracker = new ProductVisibilityTracker();
            this.productTracker.setListContext(listName, listId);
        },
        /**
         * Додає товари для відстеження
         * @param {Array} products - Масив товарів
         * @param {string} containerSelector - Селектор контейнера з товарами
         */
        trackProducts(products, containerSelector = '.products__wrapper_products') {
            if (!this.productTracker) {
                console.warn('Product tracker не ініціалізований');
                return;
            }
            if (!products || !Array.isArray(products)) {
                console.warn('Невірні дані товарів:', products);
                return;
            }
            // Чекаємо наступний тик для того, щоб DOM оновився
            this.$nextTick(() => {
                const container = this.$el.querySelector(containerSelector);
                if (!container) {
                    console.warn('Контейнер товарів не знайдено:', containerSelector);
                    return;
                }
                const productElements = container.querySelectorAll('.products__wrapper_products-product, .products__wrapper-item, .card');
                productElements.forEach((element, index) => {
                    if (products[index]) {
                        const product = products[index];
                        // Встановлюємо data-product-id з правильним ID товару
                        if (product.id) {
                            element.setAttribute('data-product-id', product.id);
                        } else {
                        }
                        // Додаємо додаткові data-атрибути для кращої ідентифікації
                        if (product.title || product.name) {
                            element.setAttribute('data-product-title', product.title || product.name);
                        }
                        if (product.category?.name) {
                            element.setAttribute('data-product-category', product.category.name);
                        }
                        if (product.productBrand?.title || product.brand?.title) {
                            element.setAttribute('data-product-brand', product.productBrand?.title || product.brand?.title);
                        }
                        
                        this.productTracker.addProduct(element, product);
                    }
                });
            });
        },
        /**
         * Додає slider контейнер для відстеження
         * @param {Array} products - Масив товарів
         * @param {string} containerSelector - Селектор slider контейнера
         * @param {string} listName - Назва списку товарів
         * @param {string} listId - ID списку товарів
         */
        trackSliderProducts(products, containerSelector = '.slider-products__wrapper', listName = 'Slider Products', listId = 'slider-products') {
            if (!this.productTracker) {
                return;
            }
            if (!products || !Array.isArray(products)) {
                return;
            }
            // Чекаємо наступний тик для того, щоб DOM оновився
            this.$nextTick(() => {
                const container = this.$el.querySelector(containerSelector);
                if (!container) {
                    return;
                }
                // Додаємо весь контейнер для відстеження
                this.productTracker.addSliderContainer(container, products, listName, listId);
            });
        },
        /**
         * Встановлює контекст списку товарів
         * @param {string} listName - Назва списку
         * @param {string} listId - ID списку
         */
        setListContext(listName, listId) {
            if (this.productTracker) {
                this.productTracker.setListContext(listName, listId);
            }
        },
        /**
         * Очищає відстеження (корисно при зміні сторінки)
         */
        resetTracking() {
            if (this.productTracker) {
                this.productTracker.reset();
            }
        },
        /**
         * Ручне відстеження всіх видимих товарів (для тестування)
         */
        trackAllVisibleProducts() {
            if (this.productTracker) {
                this.productTracker.trackAllVisibleProducts();
            }
        },
        /**
         * Глобальне встановлення data-product-id на всіх елементах товарів
         * @param {Array} products - Масив товарів (опціонально)
         */
        setGlobalProductIds(products = null) {
            
            // Якщо передано масив товарів, використовуємо його
            if (products && Array.isArray(products)) {
                this.trackProducts(products);
                return;
            }
            
            // Інакше шукаємо всі елементи товарів на сторінці
            const allProductElements = document.querySelectorAll('.products__wrapper_products-product, .products__wrapper-item, .card, [class*="product"]');
            
            allProductElements.forEach((element, index) => {
                // Перевіряємо, чи вже є data-product-id
                if (element.hasAttribute('data-product-id')) {
                    return;
                }
                
                // Спробуємо знайти ID товару в різних місцях
                let productId = null;
                
                // 1. Шукаємо в data-атрибутах
                productId = element.getAttribute('data-id') || 
                           element.getAttribute('data-product') ||
                           element.getAttribute('data-item-id');
                
                // 2. Шукаємо в Vue компоненті
                if (!productId && element.__vue__) {
                    const vueInstance = element.__vue__;
                    const vueData = vueInstance.$props?.item || 
                                   vueInstance.$props?.product || 
                                   vueInstance.$data?.item || 
                                   vueInstance.$data?.product;
                    if (vueData && vueData.id) {
                        productId = vueData.id;
                    }
                }
                
                // 3. Шукаємо в тексті або інших атрибутах
                if (!productId) {
                    const href = element.getAttribute('href');
                    if (href) {
                        const idMatch = href.match(/\/product\/(\d+)/) || href.match(/id[=\/](\d+)/);
                        if (idMatch) {
                            productId = idMatch[1];
                        }
                    }
                }
                
                // Встановлюємо data-product-id, якщо знайшли ID
                if (productId) {
                    element.setAttribute('data-product-id', productId);
                } else {
                }
            });
        }
    }
};

// Глобальна функція для встановлення data-product-id на всіх елементах товарів
window.setGlobalProductIds = function(products = null) {
    
    // Якщо передано масив товарів, використовуємо його
    if (products && Array.isArray(products)) {
        
        // Знаходимо всі елементи товарів
        const allProductElements = document.querySelectorAll('.products__wrapper_products-product, .products__wrapper-item, .card, [class*="product"]');
        
        allProductElements.forEach((element, index) => {
            if (products[index]) {
                const product = products[index];
                if (product.id) {
                    element.setAttribute('data-product-id', product.id);
                }
            }
        });
        return;
    }
    
    // Інакше шукаємо всі елементи товарів на сторінці
    const allProductElements = document.querySelectorAll('.products__wrapper_products-product, .products__wrapper-item, .card, [class*="product"]');
    
    let successCount = 0;
    let skipCount = 0;
    let failCount = 0;
    
    allProductElements.forEach((element, index) => {
        // Перевіряємо, чи вже є data-product-id
        if (element.hasAttribute('data-product-id')) {
            skipCount++;
            return;
        }
        
        // Спробуємо знайти ID товару в різних місцях
        let productId = null;
        
        // 1. Шукаємо в data-атрибутах
        productId = element.getAttribute('data-id') || 
                   element.getAttribute('data-product') ||
                   element.getAttribute('data-item-id');
        
        // 2. Шукаємо в Vue компоненті
        if (!productId && element.__vue__) {
            const vueInstance = element.__vue__;
            const vueData = vueInstance.$props?.item || 
                           vueInstance.$props?.product || 
                           vueInstance.$data?.item || 
                           vueInstance.$data?.product ||
                           vueInstance.currentProduct;
            if (vueData && vueData.id) {
                productId = vueData.id;
            }
        }
        
        // 3. Шукаємо в href або інших атрибутах
        if (!productId) {
            const href = element.getAttribute('href');
            if (href) {
                const idMatch = href.match(/\/product\/(\d+)/) || href.match(/id[=\/](\d+)/);
                if (idMatch) {
                    productId = idMatch[1];
                }
            }
        }
        
        // 4. Шукаємо в дочірніх елементах
        if (!productId) {
            const childWithId = element.querySelector('[data-id], [data-product], [data-item-id]');
            if (childWithId) {
                productId = childWithId.getAttribute('data-id') || 
                           childWithId.getAttribute('data-product') ||
                           childWithId.getAttribute('data-item-id');
            }
        }
        
        // Встановлюємо data-product-id, якщо знайшли ID
        if (productId) {
            element.setAttribute('data-product-id', productId);
            successCount++;
        } else {
            failCount++;
        }
    });

    return {
        total: allProductElements.length,
        success: successCount,
        skipped: skipCount,
        failed: failCount
    };
};

// Глобальна функція для перевірки всіх data-product-id на сторінці
window.checkAllProductIds = function() {
    console.log('🔍 [GLOBAL] checkAllProductIds: Перевірка всіх data-product-id на сторінці');
    
    const allProductElements = document.querySelectorAll('.products__wrapper_products-product, .products__wrapper-item, .card, [class*="product"]');
    console.log('🔍 [GLOBAL] Знайдено елементів товарів:', allProductElements.length);
    
    const results = {
        withDataProductId: [],
        withoutDataProductId: [],
        withVueData: [],
        withOtherIds: []
    };
    
    allProductElements.forEach((element, index) => {
        const dataProductId = element.getAttribute('data-product-id');
        const vueData = element.__vue__ ? {
            $props: element.__vue__.$props,
            $data: element.__vue__.$data,
            currentProduct: element.__vue__.currentProduct
        } : null;
        
        const otherIds = {
            dataId: element.getAttribute('data-id'),
            dataProduct: element.getAttribute('data-product'),
            dataItemId: element.getAttribute('data-item-id'),
            href: element.getAttribute('href')
        };
        
        if (dataProductId) {
            results.withDataProductId.push({
                index: index + 1,
                element: element,
                dataProductId: dataProductId,
                vueData: vueData,
                otherIds: otherIds
            });
        } else {
            results.withoutDataProductId.push({
                index: index + 1,
                element: element,
                vueData: vueData,
                otherIds: otherIds
            });
        }
        
        if (vueData && (vueData.$props?.item?.id || vueData.$props?.product?.id || vueData.currentProduct?.id)) {
            results.withVueData.push({
                index: index + 1,
                element: element,
                vueData: vueData
            });
        }
        
        if (otherIds.dataId || otherIds.dataProduct || otherIds.dataItemId) {
            results.withOtherIds.push({
                index: index + 1,
                element: element,
                otherIds: otherIds
            });
        }
    });
    
    console.log('🔍 [GLOBAL] Результати перевірки:', {
        зDataProductId: results.withDataProductId.length,
        безDataProductId: results.withoutDataProductId.length,
        зVueData: results.withVueData.length,
        зІншимиIds: results.withOtherIds.length
    });
    
    if (results.withoutDataProductId.length > 0) {
        console.log('🔍 [GLOBAL] Елементи без data-product-id:', results.withoutDataProductId);
    }
    
    if (results.withVueData.length > 0) {
        console.log('🔍 [GLOBAL] Елементи з Vue даними:', results.withVueData);
    }
    
    return results;
};
